import {
  createContext,
  FormEvent,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";

interface User {
  actorId: string;
  userId: string;
  displayName?: string | null;
  locality?: string | null;
  postalCode?: string | null;
  availabilityStatus?: string;
}
interface AuthValue {
  user: User | null;
  ready: boolean;
  refresh: () => Promise<void>;
}
interface Skill {
  id: string;
  name: string;
  confidence: number;
  riskClass: string;
  experienceNote?: string | null;
  helpModes?: string[];
  yearsExperience?: number | null;
  nodeKind?: string;
  safetyTier?: string;
  profileSelectable?: number | boolean;
  scopeNote?: string | null;
  facets?: TaxonomyFacet[];
  allowedModes?: string[];
  modeOptions?: TaxonomyMode[];
  facetOptions?: TaxonomyFacet[];
}
type SkillSelectionSource =
  | "saved_legacy"
  | "description"
  | "search"
  | "browse"
  | "clarification";
interface TaxonomyFacet {
  id: string;
  dimensionId: string;
  label: string;
  description?: string | null;
  dimensionLabel?: string;
  maxValues?: number;
}
interface TaxonomyMode {
  id: string;
  label: string;
  description: string;
}
interface TaxonomyItem {
  id: string;
  name: string;
  label?: string;
  path: string[];
  description?: string | null;
  riskClass: string;
  nodeKind: string;
  safetyTier: string;
  profileSelectable: boolean;
  requestSelectable?: boolean;
  scopeNote?: string | null;
  allowedModes: string[];
  modeOptions?: TaxonomyMode[];
  allowedFacetDimensions?: string[];
  facetOptions?: TaxonomyFacet[];
  confidence?: number;
  reason?: string;
  alreadySelected?: boolean;
}
interface TaxonomyGuidance {
  state: "clear" | "too_broad" | "low_confidence" | "no_match";
  message: string;
  clarification: {
    id: string;
    prompt: string;
    responseType: string;
    examples: string[];
  } | null;
  browseParents: string[];
}
interface RequestItem {
  id: string;
  title: string;
  publicSummary: string;
  status: string;
  matchCount: number;
  conversationCount: number;
  createdAt: string;
}
interface Match {
  id: string;
  status: string;
  requestId: string;
  title: string;
  publicSummary: string;
  postalCode: string;
  requesterName: string;
  reasonCodes: string[];
}
interface ConversationListItem {
  id: string;
  status: string;
  requestId: string;
  title: string;
  otherName: string;
  lastMessageAt: string | null;
}
interface ConversationDetail {
  conversation: {
    id: string;
    status: string;
    requestId: string;
    participants: Array<{ actorId: string; displayName: string; role: string }>;
  };
  messages: Array<{
    id: string;
    senderActorId: string;
    senderName: string;
    body: string;
    createdAt: string;
    transport: string;
  }>;
}

const AuthContext = createContext<AuthValue | null>(null);
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData))
    headers.set("content-type", "application/json");
  const result = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });
  const data = (await result.json().catch(() => ({}))) as {
    error?: string;
  } & T;
  if (!result.ok)
    throw new Error(data.error || "Something did not work. Please try again.");
  return data;
}
function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("Auth context missing");
  return value;
}
function useAsyncData<T>(
  path: string,
  fallback: T,
): [T, boolean, string | null, () => Promise<void>] {
  const [data, setData] = useState<T>(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to load this right now.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, [path]);
  return [data, loading, error, load];
}
function formatDate(value?: string | null): string {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
      }).format(new Date(value))
    : "Recently";
}
function ErrorBox({ error }: { error: string | null }) {
  return error ? (
    <div className="error" role="alert">
      {error}
    </div>
  ) : null;
}
function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="alert" role="status">
      {children}
    </div>
  );
}

function RouteMeta() {
  const location = useLocation();
  const firstRoute = useRef(true);
  useEffect(() => {
    const pages: Record<string, { title: string; description: string }> = {
      "/": {
        title: "Stuff.Repair — Someone around here probably knows how.",
        description:
          "Private, practical connections to nearby people who may know how to help.",
      },
      "/how-it-works": {
        title: "How Stuff.Repair works | Private practical help",
        description:
          "Describe a practical problem, confirm relevant skills, and choose whether to connect privately.",
      },
      "/privacy": {
        title: "Privacy on Stuff.Repair | Your contact details stay private",
        description:
          "Learn how Stuff.Repair keeps account email, exact addresses, and conversations private by default.",
      },
      "/trust": {
        title: "Contextual trust on Stuff.Repair",
        description:
          "Stuff.Repair shows relevant interaction evidence, not public star ratings or global person scores.",
      },
      "/signin": {
        title: "Sign in | Stuff.Repair",
        description:
          "Sign in to Stuff.Repair with a private, passwordless email link.",
      },
      "/verify": {
        title: "Verify sign-in | Stuff.Repair",
        description:
          "Complete your private passwordless sign-in to Stuff.Repair.",
      },
    };
    const privateRoute =
      /^\/(dashboard|help|skills|matches|conversations|settings|onboarding|signin|verify|delete)/.test(
        location.pathname,
      );
    const detail = pages[location.pathname] || {
      title: privateRoute ? "My commons | Stuff.Repair" : "Stuff.Repair",
      description:
        "Private, practical connections to nearby people who may know how to help.",
    };
    document.title = detail.title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", detail.description);
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute(
        "href",
        `https://stuff.repair${location.pathname === "/" ? "/" : location.pathname}`,
      );
    document
      .querySelector('meta[name="robots"]')
      ?.setAttribute(
        "content",
        privateRoute ? "noindex,nofollow" : "index,follow",
      );
    if (firstRoute.current) firstRoute.current = false;
    else
      requestAnimationFrame(() =>
        document.getElementById("main")?.focus({ preventScroll: true }),
      );
  }, [location.pathname]);
  return null;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const refresh = async () => {
    try {
      const result = await api<{ user: User | null }>("/api/auth/session");
      setUser(result.user);
    } finally {
      setReady(true);
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  return (
    <AuthContext.Provider value={{ user, ready, refresh }}>
      <Shell>
        <RouteMeta />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/verify" element={<Verify />} />
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <Onboarding />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/help"
            element={
              <RequireAuth>
                <AskForHelp />
              </RequireAuth>
            }
          />
          <Route
            path="/requests/:requestId/close"
            element={
              <RequireAuth>
                <Closeout />
              </RequireAuth>
            }
          />
          <Route
            path="/skills"
            element={
              <RequireAuth>
                <Skills />
              </RequireAuth>
            }
          />
          <Route
            path="/matches"
            element={
              <RequireAuth>
                <Matches />
              </RequireAuth>
            }
          />
          <Route
            path="/conversations"
            element={
              <RequireAuth>
                <Conversations />
              </RequireAuth>
            }
          />
          <Route
            path="/conversations/:conversationId"
            element={
              <RequireAuth>
                <Conversation />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <Settings />
              </RequireAuth>
            }
          />
          <Route
            path="/delete-account"
            element={
              <RequireAuth>
                <DeleteAccount />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <Admin />
              </RequireAuth>
            }
          />
          <Route
            path="/us/:region/:locality/:postalCode"
            element={<LocationPage />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Shell>
    </AuthContext.Provider>
  );
}
function Shell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  const appPath = [
    "/dashboard",
    "/help",
    "/skills",
    "/matches",
    "/conversations",
    "/settings",
  ].some((path) => location.pathname.startsWith(path));
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="header-inner">
          <Link className="brand" to="/">
            <span className="brand-mark">C</span>Stuff.Repair
          </Link>
          <nav className="desktop-nav" aria-label="Main navigation">
            <Link to="/how-it-works">How it works</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/trust">Contextual trust</Link>
            {user ? (
              <Link className="nav-cta" to="/dashboard">
                My commons
              </Link>
            ) : (
              <Link className="nav-cta" to="/signin">
                Sign in
              </Link>
            )}
          </nav>
          <div className="mobile-nav">
            <button
              className="button small"
              aria-expanded={menu}
              aria-controls="mobile-menu"
              onClick={() => setMenu(!menu)}
            >
              Menu
            </button>
            {menu && (
              <nav id="mobile-menu" aria-label="Mobile navigation">
                <Link to="/how-it-works" onClick={() => setMenu(false)}>
                  How it works
                </Link>
                <Link to="/privacy" onClick={() => setMenu(false)}>
                  Privacy
                </Link>
                <Link to="/trust" onClick={() => setMenu(false)}>
                  Contextual trust
                </Link>
                <Link
                  className="mobile-nav-cta"
                  to={user ? "/dashboard" : "/signin"}
                  onClick={() => setMenu(false)}
                >
                  {user ? "My commons" : "Sign in"}
                </Link>
              </nav>
            )}
          </div>
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className={appPath ? "main app-shell" : "main"}
      >
        {children}
      </main>
      <footer className="footer">
        <div className="footer-inner">
          <span>Stuff.Repair is a work-in-progress community tool.</span>
          <nav aria-label="Footer">
            <Link to="/privacy">Privacy</Link>
            <Link to="/how-it-works">How it works</Link>
            <a href="mailto:support@stuff.repair">Support</a>
          </nav>
        </div>
      </footer>
    </>
  );
}
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="loading">Loading your account…</div>;
  return user ? (
    <>{children}</>
  ) : (
    <Navigate
      replace
      to={`/signin?returnTo=${encodeURIComponent(location.pathname)}`}
    />
  );
}
function PublicCta() {
  const { user } = useAuth();
  return (
    <div className="hero-actions">
      <Link
        className="button primary"
        to={user ? "/help" : "/signin?returnTo=/help"}
      >
        Ask for help
      </Link>
      <Link
        className="button"
        to={user ? "/skills" : "/signin?returnTo=/skills"}
      >
        I can help with something
      </Link>
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const start = (event: FormEvent) => {
    event.preventDefault();
    if (draft.trim())
      sessionStorage.setItem("local-commons-request-draft", draft.trim());
    navigate("/help");
  };
  return (
    <>
      <section className="hero hero-home">
        <div className="hero-copy">
          <p className="eyebrow eyebrow-with-mark">
            <span aria-hidden="true" /> Practical help, kept private
          </p>
          <h1>Someone around here probably knows how.</h1>
          <p className="lede">
            Describe what is going wrong. Stuff.Repair connects the question
            with nearby people who have chosen to help with that kind of thing.
          </p>
          <form onSubmit={start} className="hero-form">
            <label className="field">
              <span className="field-label">What do you need help with?</span>
              <textarea
                aria-label="What do you need help with?"
                placeholder="My mower starts, but it dies when I take the choke off…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </label>
            <button className="button primary" type="submit">
              Find a useful starting point <span aria-hidden="true">→</span>
            </button>
          </form>
          <p className="hero-note">
            <span aria-hidden="true">◌</span> Your account email is not shown to
            other members by default. Neither is your exact location.
          </p>
        </div>
        <div className="hero-visual" aria-label="An example possible match">
          <div className="visual-orbit orbit-one" aria-hidden="true" />
          <div className="visual-orbit orbit-two" aria-hidden="true" />
          <div className="visual-pin pin-one" aria-hidden="true" />
          <div className="visual-pin pin-two" aria-hidden="true" />
          <aside className="hero-card">
            <div className="hero-card-inner">
              <div className="match-label">
                <span aria-hidden="true">✦</span> Possible fit nearby
              </div>
              <h3>2004 Honda Shadow</h3>
              <p>Starts with choke, dies when choke is removed.</p>
              <div className="mini-rule" />
              <p className="quiet">Online help preferred · Nearby</p>
              <p className="match-reason">
                <strong>Why this may fit</strong>
                <br />
                Motorcycle carburetor and fuel-system skills.
              </p>
            </div>
          </aside>
          <div className="signal-card" aria-hidden="true">
            <span>Skill signal</span>
            <strong>Fuel + carburetion</strong>
          </div>
        </div>
      </section>
      <section className="principles-strip" aria-label="How Stuff.Repair works">
        <div>
          <span className="principle-number">01</span>
          <p>Questions in plain language</p>
        </div>
        <div>
          <span className="principle-number">02</span>
          <p>Skills before identities</p>
        </div>
        <div>
          <span className="principle-number">03</span>
          <p>Contact only by choice</p>
        </div>
      </section>
      <section className="section section-principles">
        <div className="section-inner">
          <div className="section-heading">
            <p className="eyebrow">A different kind of connection</p>
            <h2>People discover skills, not people.</h2>
            <p className="lede">
              Start with the thing that needs doing. The rest stays on a
              need-to-know basis.
            </p>
          </div>
          <div className="three-col principle-cards">
            <div className="card">
              <span className="card-index" aria-hidden="true">
                A
              </span>
              <h3>Write naturally</h3>
              <p>
                Explain what is going wrong in your own words. You do not need
                to understand a category tree first.
              </p>
            </div>
            <div className="card">
              <span className="card-index" aria-hidden="true">
                B
              </span>
              <h3>Choose your boundaries</h3>
              <p>
                People decide what kinds of help they offer, where, and whether
                they are available right now.
              </p>
            </div>
            <div className="card">
              <span className="card-index" aria-hidden="true">
                C
              </span>
              <h3>Keep details private</h3>
              <p>
                Contact information and exact home locations are not part of
                ordinary matching.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="section section-help">
        <div className="section-inner two-col">
          <div className="helper-copy">
            <p className="eyebrow">For people who know the thing</p>
            <h2>Are you the person people call?</h2>
            <p className="lede">
              Share what you know. We turn it into a few skills you can confirm,
              then only bring you relevant requests.
            </p>
            <PublicCta />
          </div>
          <div className="privacy-callout">
            <span className="privacy-mark" aria-hidden="true">
              ⌁
            </span>
            <div>
              <span className="callout-kicker">A practical boundary</span>
              <strong>Privacy without empty promises</strong>
              <p>
                We limit what other members can see and make sharing direct
                contact an explicit choice. Stuff.Repair does not certify people
                or guarantee outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
function HowItWorks() {
  return (
    <div className="page">
      <p className="eyebrow">How it works</p>
      <h1>Useful help starts with a clear question.</h1>
      <div className="three-col" style={{ marginTop: "2rem" }}>
        <div className="card">
          <h3>1. Describe the problem</h3>
          <p>
            Use normal language. We suggest a small set of relevant skills for
            you to confirm.
          </p>
        </div>
        <div className="card">
          <h3>2. Find possible fits</h3>
          <p>
            People who opted in to the right skills and area can choose whether
            to respond.
          </p>
        </div>
        <div className="card">
          <h3>3. Keep the conversation yours</h3>
          <p>
            Talk in a private Stuff.Repair conversation. Share direct details
            only if you decide to.
          </p>
        </div>
      </div>
      <div className="form-card">
        <h2>Built for reciprocity</h2>
        <p>
          The same person can ask a refrigerator question today and help with a
          carburetor question tomorrow. There are no permanent helper or helpee
          labels.
        </p>
        <PublicCta />
      </div>
    </div>
  );
}
function Privacy() {
  return (
    <div className="page narrow">
      <p className="eyebrow">Privacy in plain language</p>
      <h1>Your email is account infrastructure, not a profile field.</h1>
      <p className="lede">
        Other members do not see your account email, phone number, exact
        address, or authentication identifiers by default.
      </p>
      <div className="form-card">
        <h2>What people can see</h2>
        <p>
          In a relevant connection, another person may see the display name, a
          general area, a request summary, selected ways of helping, and
          contextual interaction information. We do not publish a directory of
          people.
        </p>
        <h2>What is collected</h2>
        <p>
          Stuff.Repair needs an email to sign you in and notify you; a display
          identity, age confirmation, and approximate area to operate the
          service; and the information you choose to add to requests, skills,
          conversations, and feedback.
        </p>
        <h2>Your choices</h2>
        <p>
          You can pause matching, control notification previews, block someone,
          report a concern, export eligible account data, and request deletion.
          The product is being built with these controls from the start.
        </p>
      </div>
    </div>
  );
}
function Trust() {
  return (
    <div className="page narrow">
      <p className="eyebrow">Contextual trust</p>
      <h1>Evidence for this interaction, not a score for a person.</h1>
      <p className="lede">
        A useful question is whether someone may fit this particular
        situation—not whether they are a four-point-something human.
      </p>
      <div className="form-card">
        <h2>What may be shown</h2>
        <div className="card subtle">
          <h3>May be a useful fit</h3>
          <p>
            Repeatedly helpful with older motorcycle fuel and carburetor
            questions.
            <br />
            Often helps people reach a clear next step.
            <br />
            Online troubleshooting and teaching are available.
          </p>
        </div>
        <p>
          There are no public stars, rankings, leaderboards, or global trust
          numbers. A new person is simply new—not bad. Serious safety reports
          are handled separately from ordinary interaction outcomes.
        </p>
      </div>
    </div>
  );
}

function SignIn() {
  const { user } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<{
    developmentLink?: string;
    developmentCode?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  if (user) return <Navigate to="/dashboard" replace />;
  const returnTo =
    new URLSearchParams(location.search).get("returnTo") || "/dashboard";
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const data = await api<{
        developmentLink?: string;
        developmentCode?: string;
      }>("/api/auth/request", {
        method: "POST",
        body: JSON.stringify({ email, returnTo }),
      });
      setResult(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send a link.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="page narrow">
      <p className="eyebrow">Sign in without a password</p>
      <h1>Enter your email to continue.</h1>
      <p className="lede">
        We use it to sign you in and notify you. Other members do not see it
        unless you choose to share it later.
      </p>
      <form className="form-card" onSubmit={submit}>
        <label className="field">
          <span>Email address</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <ErrorBox error={error} />
        <button className="button primary" disabled={saving}>
          {saving ? "Sending…" : "Email me a sign-in link"}
        </button>
        {result && (
          <div className="alert success" role="status">
            <strong>Check your email.</strong>
            <br />
            In local development, use{" "}
            <Link to={result.developmentLink || "/verify"}>
              this secure test link
            </Link>{" "}
            or enter code <code>{result.developmentCode}</code> on the
            verification screen. The same neutral response is shown whether or
            not an account exists.
          </div>
        )}
      </form>
    </div>
  );
}
function Verify() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const result = await api<{ next: string }>("/api/auth/verify", {
        method: "POST",
        body: JSON.stringify(token ? { token } : { code }),
      });
      await refresh();
      navigate(result.next, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to verify that link.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="page narrow">
      <p className="eyebrow">One-time verification</p>
      <h1>Check your email.</h1>
      <p className="lede">
        Links and codes expire quickly and can only be used once.
      </p>
      <form className="form-card" onSubmit={submit}>
        {token ? (
          <div className="alert success" role="status">
            <strong>Your secure link is ready.</strong>
            <br />
            This link already includes your one-time code. Select Continue to
            finish signing in; there is nothing to paste or type.
          </div>
        ) : (
          <label className="field">
            <span>Verification code</span>
            <input
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCDEFGH"
            />
          </label>
        )}
        <ErrorBox error={error} />
        <button className="button primary" disabled={saving}>
          {saving ? "Verifying…" : token ? "Continue securely" : "Verify code"}
        </button>
      </form>
    </div>
  );
}
function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [postalCode, setPostalCode] = useState(user?.postalCode || "");
  const [locality, setLocality] = useState(user?.locality || "");
  const [checks, setChecks] = useState({
    terms: false,
    privacy: false,
    age: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api("/api/onboarding", {
        method: "POST",
        body: JSON.stringify({
          displayName,
          postalCode,
          locality,
          termsAccepted: checks.terms,
          privacyAccepted: checks.privacy,
          age18: checks.age,
        }),
      });
      await refresh();
      const draft = sessionStorage.getItem("local-commons-request-draft");
      navigate(draft ? "/help" : "/dashboard");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to save your details.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="page narrow">
      <p className="eyebrow">A few basics</p>
      <h1>Set up your local profile.</h1>
      <p className="lede">
        You can add skills later. We only need a display name and an approximate
        area to start.
      </p>
      <form className="form-card" onSubmit={submit}>
        <label className="field">
          <span>Display first name or nickname</span>
          <input
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Postal code</span>
          <input
            required
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Locality (optional)</span>
          <input
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
          />
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={checks.terms}
            onChange={(e) => setChecks({ ...checks, terms: e.target.checked })}
          />{" "}
          <span>I agree to the Terms of Service.</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={checks.privacy}
            onChange={(e) =>
              setChecks({ ...checks, privacy: e.target.checked })
            }
          />{" "}
          <span>I have read the Privacy Notice.</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={checks.age}
            onChange={(e) => setChecks({ ...checks, age: e.target.checked })}
          />{" "}
          <span>I confirm I am 18 or older.</span>
        </label>
        <ErrorBox error={error} />
        <button className="button primary" disabled={saving}>
          {saving ? "Saving…" : "Continue"}
        </button>
      </form>
    </div>
  );
}

function AppFrame({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const location = useLocation();
  const nav = [
    ["/dashboard", "Overview"],
    ["/help", "Ask for help"],
    ["/skills", "Skills & ways I help"],
    ["/matches", "Possible matches"],
    ["/conversations", "Conversations"],
    ["/settings", "Settings"],
  ];
  return (
    <div className="app-layout">
      <nav className="app-nav card" aria-label="Account navigation">
        <span className="app-nav-label">Your space</span>
        {nav.map(([href, label]) => (
          <Link
            key={href}
            className={
              location.pathname === href ||
              (href === "/conversations" &&
                location.pathname.startsWith("/conversations/"))
                ? "active"
                : ""
            }
            to={href}
          >
            {label}
          </Link>
        ))}
      </nav>
      <section className="app-main">
        <div className="app-heading">
          <div>
            <p className="eyebrow">My commons</p>
            <h1>{title}</h1>
          </div>
          {action}
        </div>
        {children}
      </section>
    </div>
  );
}
function Dashboard() {
  const { user } = useAuth();
  const [requests, loadingRequests, requestError] = useAsyncData<{
    requests: RequestItem[];
  }>("/api/requests/mine", { requests: [] });
  const [matches, loadingMatches] = useAsyncData<{ matches: Match[] }>(
    "/api/matches",
    { matches: [] },
  );
  const [conversations, loadingConversations] = useAsyncData<{
    conversations: ConversationListItem[];
  }>("/api/conversations", { conversations: [] });
  return (
    <AppFrame
      title={
        user?.displayName
          ? `Welcome back, ${user.displayName}.`
          : "Your commons"
      }
      action={
        <Link className="button primary" to="/help">
          Ask for help
        </Link>
      }
    >
      <div className="privacy-callout">
        <span className="privacy-mark" aria-hidden="true">
          i
        </span>
        <div>
          <strong>Your privacy controls are active.</strong>
          <p>
            Contact information is not shared through ordinary matching. You can
            pause requests, block people, or change notification preferences in
            Settings.
          </p>
        </div>
      </div>
      <div className="two-col" style={{ marginTop: "1.2rem" }}>
        <section className="card">
          <h3>Your help requests</h3>
          {loadingRequests ? (
            <p className="quiet">Loading…</p>
          ) : requestError ? (
            <ErrorBox error={requestError} />
          ) : requests.requests.length ? (
            <div className="list">
              {requests.requests.slice(0, 3).map((item) => (
                <div className="list-item" key={item.id}>
                  <div>
                    <strong>{item.title}</strong>
                    <p className="quiet">
                      {item.matchCount} possible{" "}
                      {item.matchCount === 1 ? "match" : "matches"} ·{" "}
                      {item.conversationCount} conversation
                      {item.conversationCount === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div>
                    <span
                      className={`status ${item.status === "closed" ? "closed" : ""}`}
                    >
                      {item.status}
                    </span>
                    {!["closed", "resolved", "next_step", "cancelled"].includes(
                      item.status,
                    ) && (
                      <Link
                        className="button small"
                        to={`/requests/${item.id}/close`}
                      >
                        Close out
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">
              No help requests yet.
              <br />
              <Link to="/help">Describe what you need help with.</Link>
            </div>
          )}
        </section>
        <section className="card">
          <h3>Possible matches</h3>
          {loadingMatches ? (
            <p className="quiet">Loading…</p>
          ) : matches.matches.length ? (
            <div className="list">
              {matches.matches.slice(0, 3).map((item) => (
                <div className="list-item" key={item.id}>
                  <div>
                    <strong>{item.title}</strong>
                    <p className="quiet">{item.publicSummary}</p>
                  </div>
                  <Link className="button small" to="/matches">
                    View
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">
              When a request fits your skills and area, it will appear here.
            </div>
          )}
        </section>
      </div>
      <section className="card" style={{ marginTop: "1.2rem" }}>
        <h3>Conversations</h3>
        {loadingConversations ? (
          <p className="quiet">Loading…</p>
        ) : conversations.conversations.length ? (
          <div className="list">
            {conversations.conversations.slice(0, 3).map((item) => (
              <div className="list-item" key={item.id}>
                <div>
                  <strong>{item.otherName || "Conversation"}</strong>
                  <p className="quiet">
                    {item.title} · {formatDate(item.lastMessageAt)}
                  </p>
                </div>
                <Link className="button small" to={`/conversations/${item.id}`}>
                  Open
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty">
            Private conversations begin only after someone chooses to respond.
          </div>
        )}
      </section>
    </AppFrame>
  );
}
function AskForHelp() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [description, setDescription] = useState(
    () => sessionStorage.getItem("local-commons-request-draft") || "",
  );
  const [clarification, setClarification] = useState("");
  const [postalCode, setPostalCode] = useState(user?.postalCode || "");
  const [urgency, setUrgency] = useState("flexible");
  const [ideas, setIdeas] = useState<TaxonomyItem[]>([]);
  const [guidance, setGuidance] = useState<TaxonomyGuidance | null>(null);
  const [restrictedMessage, setRestrictedMessage] = useState<string | null>(null);
  const [knownItems, setKnownItems] = useState<Record<string, TaxonomyItem>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [facets, setFacets] = useState<string[]>([]);
  const [browseItems, setBrowseItems] = useState<TaxonomyItem[]>([]);
  const [browseParent, setBrowseParent] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [searchItems, setSearchItems] = useState<TaxonomyItem[]>([]);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [phraseSent, setPhraseSent] = useState(false);
  const [waysToHelp, setWaysToHelp] = useState({
    remote: true,
    inPerson: true,
    community: true,
    barter: false,
    paid: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mergeKnownItems = (items: TaxonomyItem[]) =>
    setKnownItems((current) => ({
      ...current,
      ...Object.fromEntries(items.map((item) => [item.id, item])),
    }));

  const loadBrowse = async (parentId: string | null) => {
    setCatalogBusy(true);
    try {
      const query = parentId ? `&parentId=${encodeURIComponent(parentId)}` : "";
      const data = await api<{ items: TaxonomyItem[] }>(
        `/api/taxonomy?surface=request&limit=24${query}`,
      );
      setBrowseItems(data.items);
      setBrowseParent(parentId);
      mergeKnownItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to browse skills.");
    } finally {
      setCatalogBusy(false);
    }
  };

  useEffect(() => {
    void loadBrowse(null);
  }, []);

  const addSkill = (item: TaxonomyItem) => {
    mergeKnownItems([item]);
    setSelected((current) => {
      if (current.includes(item.id)) return current;
      if (current.length >= 7) {
        setError("Choose up to 7 categories for one request.");
        return current;
      }
      return [...current, item.id];
    });
    setError(null);
  };

  const removeSkill = (categoryId: string) => {
    setSelected((current) => current.filter((id) => id !== categoryId));
    setFacets((current) => {
      const allowedElsewhere = new Set(
        selected
          .filter((id) => id !== categoryId)
          .flatMap((id) => knownItems[id]?.facetOptions || [])
          .map((facet) => facet.id),
      );
      return current.filter((facetId) => allowedElsewhere.has(facetId));
    });
  };

  const suggestCategories = async (withClarification = false) => {
    if (description.trim().length < 12) {
      setError("Describe the situation first, then ask for category ideas.");
      return;
    }
    setSuggesting(true);
    try {
      const text = [description, withClarification ? clarification : ""]
        .filter(Boolean)
        .join("\n");
      const data = await api<{
        candidates: TaxonomyItem[];
        guidance: TaxonomyGuidance;
        restrictedHandling: { message: string } | null;
      }>("/api/taxonomy/suggest", {
        method: "POST",
        body: JSON.stringify({
          surface: "request",
          text,
          selectedCategoryIds: selected,
          limit: 7,
        }),
      });
      setIdeas(data.candidates);
      mergeKnownItems(data.candidates);
      setGuidance(data.guidance);
      setRestrictedMessage(data.restrictedHandling?.message || null);
      setPhraseSent(false);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to suggest categories.",
      );
    } finally {
      setSuggesting(false);
    }
  };

  const searchCatalog = async () => {
    if (searchText.trim().length < 2) {
      setSearchItems([]);
      setSearchMessage("Enter at least two characters to search the catalog.");
      return;
    }
    setCatalogBusy(true);
    try {
      const data = await api<{
        items: TaxonomyItem[];
        noExactMatch: boolean;
      }>(
        `/api/taxonomy/search?surface=request&q=${encodeURIComponent(searchText.trim())}&limit=12`,
      );
      setSearchItems(data.items);
      mergeKnownItems(data.items);
      setSearchMessage(
        data.noExactMatch
          ? "No approved request category matches that yet. You can suggest a phrase for private review below."
          : null,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to search skills.");
    } finally {
      setCatalogBusy(false);
    }
  };

  const availableFacets = [...new Map(
    selected
      .flatMap((id) => knownItems[id]?.facetOptions || [])
      .map((facet) => [facet.id, facet]),
  ).values()];
  const facetsByDimension = availableFacets.reduce<Record<string, TaxonomyFacet[]>>(
    (groups, facet) => {
      groups[facet.dimensionId] = [
        ...(groups[facet.dimensionId] || []),
        facet,
      ];
      return groups;
    },
    {},
  );
  const toggleFacet = (facet: TaxonomyFacet) => {
    setFacets((current) => {
      if (current.includes(facet.id))
        return current.filter((id) => id !== facet.id);
      const sameDimension = availableFacets.filter(
        (option) =>
          option.dimensionId === facet.dimensionId && current.includes(option.id),
      );
      if (sameDimension.length >= (facet.maxValues || 1)) {
        setError(
          `Choose up to ${facet.maxValues || 1} option${facet.maxValues === 1 ? "" : "s"} for ${facet.dimensionLabel || "this detail"}.`,
        );
        return current;
      }
      return [...current, facet.id];
    });
  };

  const submitPhrase = async () => {
    if (phrase.trim().length < 3) {
      setError("Add a short phrase before sending it for private review.");
      return;
    }
    try {
      await api("/api/taxonomy/suggestions", {
        method: "POST",
        body: JSON.stringify({ surface: "request", phrase: phrase.trim() }),
      });
      setPhrase("");
      setPhraseSent(true);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to send that phrase.",
      );
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected.length) {
      setError("Choose at least one approved category before posting.");
      return;
    }
    setSaving(true);
    try {
      const created = await api<{ id: string; matchesCreated: number }>(
        "/api/requests",
        {
          method: "POST",
          body: JSON.stringify({
            description,
            postalCode,
            urgency,
            remoteOk: waysToHelp.remote,
            inPersonOk: waysToHelp.inPerson,
            communityHelpOk: waysToHelp.community,
            barterOk: waysToHelp.barter,
            paidOk: waysToHelp.paid,
            skillIds: selected,
            facetValueIds: facets,
          }),
        },
      );
      sessionStorage.removeItem("local-commons-request-draft");
      navigate("/dashboard", { state: { created } });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to post this request.",
      );
    } finally {
      setSaving(false);
    }
  };
  const selectedItems = selected
    .map((id) => knownItems[id])
    .filter((item): item is TaxonomyItem => Boolean(item));
  const visibleCatalogItems = searchItems.length ? searchItems : browseItems;
  const catalogHeading = searchItems.length
    ? "Search results"
    : browseParent
      ? "Request categories in this area"
      : "Browse all request areas";
  return (
    <AppFrame title="What do you need help with?">
      <p className="lede">
        Describe the situation in your own words, then confirm approved request
        categories. This keeps matching relevant without turning a phrase into a
        public category.
      </p>
      <form className="form-card taxonomy-form" onSubmit={submit}>
        <label className="field">
          <span>Describe the problem</span>
          <textarea
            required
            minLength={12}
            maxLength={5000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="My 2004 Honda Shadow starts with the choke but dies when I take it off. It sat all winter."
          />
          <small className="help">
            Do not include an email, phone number, or exact street address. We
            remove obvious contact details from the matching summary.
          </small>
        </label>
        <button
          className="button"
          type="button"
          disabled={suggesting}
          onClick={() => void suggestCategories()}
        >
          {suggesting ? "Finding categories…" : "Find category ideas"}
        </button>

        {(ideas.length > 0 || guidance || restrictedMessage) && (
          <section className="taxonomy-panel" aria-live="polite">
            <div className="taxonomy-panel-heading">
              <div>
                <p className="eyebrow">From your description</p>
                <h3>Categories to confirm</h3>
              </div>
              <span className="taxonomy-count">{ideas.length} found</span>
            </div>
            {ideas.length > 0 && (
              <div className="taxonomy-results">
                {ideas.map((item) => (
                  <article className="taxonomy-result" key={item.id}>
                    <div>
                      <strong>{item.name}</strong>
                      {item.reason && <p className="quiet">{item.reason}</p>}
                      {item.scopeNote && (
                        <p className="taxonomy-scope">{item.scopeNote}</p>
                      )}
                    </div>
                    <button
                      className="button small"
                      type="button"
                      aria-pressed={selected.includes(item.id)}
                      onClick={() => addSkill(item)}
                    >
                      {selected.includes(item.id) ? "Added" : "Add category"}
                    </button>
                  </article>
                ))}
              </div>
            )}
            {guidance && <p className="help">{guidance.message}</p>}
            {guidance?.clarification && (
              <div className="taxonomy-clarification">
                <label className="field">
                  <span>{guidance.clarification.prompt}</span>
                  <input
                    value={clarification}
                    onChange={(e) => setClarification(e.target.value)}
                    placeholder={guidance.clarification.examples[0]}
                  />
                  <small className="help">
                    For example: {guidance.clarification.examples.join(" · ")}
                  </small>
                </label>
                <button
                  className="button small"
                  type="button"
                  disabled={suggesting || clarification.trim().length < 3}
                  onClick={() => void suggestCategories(true)}
                >
                  Use this detail
                </button>
              </div>
            )}
            {restrictedMessage && (
              <div className="taxonomy-restricted" role="status">
                <strong>Outside ordinary peer matching</strong>
                <p>{restrictedMessage}</p>
              </div>
            )}
          </section>
        )}

        <section className="taxonomy-discovery" aria-label="Approved request catalog">
          <div className="taxonomy-panel-heading">
            <div>
              <p className="eyebrow">Approved catalog</p>
              <h3>Find a specific category</h3>
            </div>
          </div>
          <div className="taxonomy-search">
            <label className="visually-hidden" htmlFor="request-catalog-search">
              Search approved request categories
            </label>
            <input
              id="request-catalog-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void searchCatalog();
                }
              }}
              placeholder="Search: carburetor, garden, phone setup…"
            />
            <button
              className="button small"
              type="button"
              disabled={catalogBusy}
              onClick={() => void searchCatalog()}
            >
              Search
            </button>
            {searchItems.length > 0 && (
              <button
                className="button tertiary"
                type="button"
                onClick={() => {
                  setSearchItems([]);
                  setSearchText("");
                  setSearchMessage(null);
                }}
              >
                Clear search
              </button>
            )}
          </div>
          {browseParent && !searchItems.length && (
            <button
              className="button tertiary"
              type="button"
              onClick={() => void loadBrowse(null)}
            >
              ← All request areas
            </button>
          )}
          <h4 className="taxonomy-subheading">{catalogHeading}</h4>
          {catalogBusy ? (
            <p className="quiet">Loading catalog…</p>
          ) : (
            <div className="taxonomy-catalog-grid">
              {visibleCatalogItems.map((item) =>
                item.nodeKind === "domain" ? (
                  <button
                    className="taxonomy-domain"
                    type="button"
                    key={item.id}
                    onClick={() => void loadBrowse(item.id)}
                  >
                    <strong>{item.name}</strong>
                    <span>{item.description || "Browse categories"}</span>
                    <small>Browse area →</small>
                  </button>
                ) : (
                  <article className="taxonomy-catalog-item" key={item.id}>
                    <div>
                      <strong>{item.name}</strong>
                      {item.path.length > 1 && (
                        <span className="taxonomy-path">
                          {item.path.slice(0, -1).join(" › ")}
                        </span>
                      )}
                      {item.scopeNote && (
                        <p className="taxonomy-scope">{item.scopeNote}</p>
                      )}
                    </div>
                    <button
                      className="button small"
                      type="button"
                      aria-pressed={selected.includes(item.id)}
                      onClick={() => addSkill(item)}
                    >
                      {selected.includes(item.id) ? "Added" : "Add"}
                    </button>
                  </article>
                ),
              )}
            </div>
          )}
          {searchMessage && <p className="help">{searchMessage}</p>}
          {(guidance?.state === "no_match" || searchMessage) && (
            <div className="taxonomy-phrase">
              <label className="field">
                <span>I do not see it</span>
                <input
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder="A short name for the request category"
                />
                <small className="help">
                  This phrase is encrypted for private catalog review. It is not
                  public or added automatically.
                </small>
              </label>
              <button
                className="button small"
                type="button"
                onClick={() => void submitPhrase()}
              >
                Send for review
              </button>
              {phraseSent && (
                <p className="taxonomy-phrase-success" role="status">
                  Thanks — it is queued for private catalog review.
                </p>
              )}
            </div>
          )}
        </section>

        <section className="taxonomy-selected" aria-labelledby="request-categories-title">
          <div className="taxonomy-panel-heading">
            <div>
              <p className="eyebrow">Your request</p>
              <h3 id="request-categories-title">Categories you have confirmed</h3>
            </div>
            <span className="taxonomy-count">{selectedItems.length} selected</span>
          </div>
          <p className="help">
            Select only approved categories that fit this situation. Matching uses
            these choices, not a new free-form category.
          </p>
          {selectedItems.length ? (
            <div className="taxonomy-selected-list">
              {selectedItems.map((item) => (
                <article className="taxonomy-selected-card" key={item.id}>
                  <div className="taxonomy-selected-title">
                    <div>
                      <h4>{item.name}</h4>
                      {item.scopeNote && (
                        <p className="taxonomy-scope">{item.scopeNote}</p>
                      )}
                    </div>
                    <button
                      className="button tertiary"
                      type="button"
                      onClick={() => removeSkill(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="taxonomy-empty">
              No category selected yet. Use ideas, search, or browse to choose
              at least one approved category.
            </div>
          )}
          {Object.entries(facetsByDimension).map(([dimensionId, options]) => (
            <fieldset className="taxonomy-choice-field" key={dimensionId}>
              <legend>{options[0]?.dimensionLabel || "Useful details"}</legend>
              <div className="chip-row">
                {options.map((facet) => (
                  <button
                    className="chip"
                    type="button"
                    key={facet.id}
                    aria-pressed={facets.includes(facet.id)}
                    onClick={() => toggleFacet(facet)}
                    title={facet.description || undefined}
                  >
                    {facet.label}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
        </section>

        <fieldset className="taxonomy-preferences">
          <legend>What kind of help would work?</legend>
          <p className="help">
            These preferences guide possible matches; no obligation is created.
          </p>
          <div className="taxonomy-preference-grid">
            {[
              ["remote", "Advice online or by message"],
              ["inPerson", "In-person help by agreement"],
              ["community", "Community / no-charge help"],
              ["barter", "Barter may be discussed"],
              ["paid", "Paid work may be discussed"],
            ].map(([key, label]) => (
              <label className="check-row" key={key}>
                <input
                  type="checkbox"
                  checked={waysToHelp[key as keyof typeof waysToHelp]}
                  onChange={(event) =>
                    setWaysToHelp((current) => ({
                      ...current,
                      [key]: event.target.checked,
                    }))
                  }
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="two-col">
          <label className="field">
            <span>Approximate postal code</span>
            <input
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </label>
          <label className="field">
            <span>When do you need help?</span>
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
            >
              <option value="flexible">Flexible</option>
              <option value="soon">Soon</option>
              <option value="today">Today</option>
            </select>
          </label>
        </div>
        <ErrorBox error={error} />
        <button className="button primary" disabled={saving}>
          {saving ? "Posting…" : "Post this request"}
        </button>
      </form>
    </AppFrame>
  );
}
function Closeout() {
  const { requestId = "" } = useParams();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const options = [
    ["resolved", "Resolved"],
    ["next_step", "I have a clear next step"],
    ["partly_helpful", "Partly helpful"],
    ["still_looking", "Still looking for help"],
    ["no_longer_needed", "No longer needed"],
    ["did_not_connect", "We did not connect"],
  ];
  const tagOptions = [
    "diagnosed/narrowed the issue",
    "explained clearly",
    "suggested a useful next step",
    "shared a resource",
    "communicated reliably",
    "helped me learn",
    "referred a professional",
  ];
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api(`/api/requests/${requestId}/close`, {
        method: "POST",
        body: JSON.stringify({ outcome, tags }),
      });
      navigate("/dashboard");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to close out this request.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <AppFrame title="Close the loop">
      <p className="lede">
        This creates private, contextual interaction evidence. It does not
        create a public rating or a global score.
      </p>
      <form className="form-card" onSubmit={submit}>
        <fieldset className="field">
          <legend>Did this connection move your problem forward?</legend>
          {options.map(([value, label]) => (
            <label className="check-row" key={value}>
              <input
                type="radio"
                name="outcome"
                value={value}
                checked={outcome === value}
                onChange={() => setOutcome(value)}
              />{" "}
              <span>{label}</span>
            </label>
          ))}
        </fieldset>
        <fieldset className="field">
          <legend>What helped? (optional)</legend>
          <div className="chip-row">
            {tagOptions.map((tag) => (
              <button
                key={tag}
                type="button"
                className="chip"
                aria-pressed={tags.includes(tag)}
                onClick={() =>
                  setTags(
                    tags.includes(tag)
                      ? tags.filter((item) => item !== tag)
                      : [...tags, tag],
                  )
                }
              >
                {tag}
              </button>
            ))}
          </div>
        </fieldset>
        <ErrorBox error={error} />
        <button className="button primary" disabled={saving}>
          {saving ? "Saving…" : "Save closeout"}
        </button>
      </form>
    </AppFrame>
  );
}

function Skills() {
  const { user } = useAuth();
  const [description, setDescription] = useState("");
  const [clarification, setClarification] = useState("");
  const [ideas, setIdeas] = useState<TaxonomyItem[]>([]);
  const [guidance, setGuidance] = useState<TaxonomyGuidance | null>(null);
  const [restrictedMessage, setRestrictedMessage] = useState<string | null>(null);
  const [knownItems, setKnownItems] = useState<Record<string, TaxonomyItem>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [sources, setSources] = useState<Record<string, SkillSelectionSource>>({});
  const [modesBySkill, setModesBySkill] = useState<Record<string, string[]>>({});
  const [facetsBySkill, setFacetsBySkill] = useState<Record<string, string[]>>({});
  const [postalCode, setPostalCode] = useState(user?.postalCode || "");
  const [onlineAnywhere, setOnlineAnywhere] = useState(false);
  const [revision, setRevision] = useState<number | null>(null);
  const [taxonomyVersion, setTaxonomyVersion] = useState<string | null>(null);
  const [browseItems, setBrowseItems] = useState<TaxonomyItem[]>([]);
  const [browseParent, setBrowseParent] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [searchItems, setSearchItems] = useState<TaxonomyItem[]>([]);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [phraseSent, setPhraseSent] = useState(false);
  const [legacyPolicyNotice, setLegacyPolicyNotice] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, loadingSaved, savedError, reloadSaved] = useAsyncData<{
    skills: Skill[];
    areas: Array<{ postalCode: string | null; onlineAnywhere: boolean }>;
    experienceNote?: string | null;
    revision?: number;
    taxonomyVersion?: string | null;
  }>("/api/skills", { skills: [], areas: [] });
  const loadedSavedSkills = useRef(false);

  const mergeKnownItems = (items: TaxonomyItem[]) =>
    setKnownItems((current) => ({
      ...current,
      ...Object.fromEntries(items.map((item) => [item.id, item])),
    }));

  const loadBrowse = async (parentId: string | null) => {
    setCatalogBusy(true);
    try {
      const query = parentId ? `&parentId=${encodeURIComponent(parentId)}` : "";
      const data = await api<{ items: TaxonomyItem[] }>(
        `/api/taxonomy?surface=profile&limit=24${query}`,
      );
      setBrowseItems(data.items);
      setBrowseParent(parentId);
      mergeKnownItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to browse skills.");
    } finally {
      setCatalogBusy(false);
    }
  };

  useEffect(() => {
    void loadBrowse(null);
  }, []);

  useEffect(() => {
    if (loadingSaved || loadedSavedSkills.current) return;
    loadedSavedSkills.current = true;
    const savedDescription =
      saved.experienceNote || saved.skills[0]?.experienceNote || "";
    if (savedDescription)
      setDescription((current) => current || savedDescription);
    setRevision(saved.revision ?? 1);
    setTaxonomyVersion(saved.taxonomyVersion || null);
    const controlledSavedSkills = saved.skills.filter(
      (skill) =>
        skill.nodeKind === "service" &&
        ["S0", "S1"].includes(skill.safetyTier || "S0") &&
        skill.profileSelectable !== 0 &&
        skill.profileSelectable !== false,
    );
    const excludedSavedSkills = saved.skills.filter(
      (skill) => !controlledSavedSkills.includes(skill),
    );
    const staleDetailCount = controlledSavedSkills.reduce((count, skill) => {
      const validModes = new Set((skill.modeOptions || []).map((mode) => mode.id));
      const validFacets = new Set(
        (skill.facetOptions || []).map((facet) => facet.id),
      );
      return (
        count +
        (skill.helpModes || []).filter((id) => !validModes.has(id)).length +
        (skill.facets || []).filter((facet) => !validFacets.has(facet.id)).length
      );
    }, 0);
    const policyNotices = [
      excludedSavedSkills.length
        ? `${excludedSavedSkills.map((skill) => skill.name).join(", ")} ${excludedSavedSkills.length === 1 ? "is" : "are"} not included in the current ordinary peer-help catalog.`
        : "",
      staleDetailCount
        ? "Some older detail choices no longer fit the current category policy and are hidden until you choose updated details."
        : "",
    ].filter(Boolean);
    if (policyNotices.length)
      setLegacyPolicyNotice(
        `${policyNotices.join(" ")} Review the remaining selections and save to update your profile.`,
      );
    const savedItems = controlledSavedSkills.map<TaxonomyItem>((skill) => ({
      id: skill.id,
      name: skill.name,
      path: [skill.name],
      description: null,
      riskClass: skill.riskClass,
      nodeKind: skill.nodeKind || "service",
      safetyTier: skill.safetyTier || "S0",
      profileSelectable: skill.profileSelectable !== 0,
      scopeNote: skill.scopeNote,
      allowedModes: skill.allowedModes || [],
      modeOptions: skill.modeOptions || [],
      facetOptions: skill.facetOptions || [],
    }));
    mergeKnownItems(savedItems);
    setSelected(savedItems.map((item) => item.id));
    setSources(
      Object.fromEntries(savedItems.map((item) => [item.id, "saved_legacy"])),
    );
    setModesBySkill(
      Object.fromEntries(
        controlledSavedSkills.map((skill) => [
          skill.id,
          (skill.helpModes || []).filter((modeId) =>
            (skill.modeOptions || []).some((mode) => mode.id === modeId),
          ),
        ]),
      ),
    );
    setFacetsBySkill(
      Object.fromEntries(
        controlledSavedSkills.map((skill) => [
          skill.id,
          (skill.facets || [])
            .filter((facet) =>
              (skill.facetOptions || []).some(
                (option) => option.id === facet.id,
              ),
            )
            .map((facet) => facet.id),
        ]),
      ),
    );
    const localArea = saved.areas.find((area) => area.postalCode);
    if (localArea?.postalCode) setPostalCode(localArea.postalCode);
    setOnlineAnywhere(saved.areas.some((area) => area.onlineAnywhere));
  }, [loadingSaved, saved]);

  const addSkill = (item: TaxonomyItem, source: SkillSelectionSource) => {
    mergeKnownItems([item]);
    setSelected((current) => {
      if (current.includes(item.id)) return current;
      if (current.length >= 16) {
        setError("Choose up to 16 skills. Remove one before adding another.");
        return current;
      }
      return [...current, item.id];
    });
    setSources((current) => ({ ...current, [item.id]: source }));
    setModesBySkill((current) => ({
      ...current,
      [item.id]:
        current[item.id] ||
        (item.allowedModes || []).filter((mode) =>
          ["online", "teach"].includes(mode),
        ),
    }));
    setSuccess(false);
    setError(null);
  };

  const removeSkill = (skillId: string) => {
    setSelected((current) => current.filter((id) => id !== skillId));
    setModesBySkill((current) => {
      const next = { ...current };
      delete next[skillId];
      return next;
    });
    setFacetsBySkill((current) => {
      const next = { ...current };
      delete next[skillId];
      return next;
    });
    setSuccess(false);
  };

  const suggestSkills = async (withClarification = false) => {
    if (description.trim().length < 8) {
      setError("Add a short description of what you know, then ask for ideas.");
      return;
    }
    setSuggesting(true);
    try {
      const text = [description, withClarification ? clarification : ""]
        .filter(Boolean)
        .join("\n");
      const data = await api<{
        candidates: TaxonomyItem[];
        guidance: TaxonomyGuidance;
        restrictedHandling: { message: string } | null;
      }>("/api/taxonomy/suggest", {
        method: "POST",
        body: JSON.stringify({
          surface: "profile",
          text,
          selectedCategoryIds: selected,
          limit: 12,
        }),
      });
      setIdeas(data.candidates);
      mergeKnownItems(data.candidates);
      setGuidance(data.guidance);
      setRestrictedMessage(data.restrictedHandling?.message || null);
      setPhraseSent(false);
      setSuccess(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to suggest skills.");
    } finally {
      setSuggesting(false);
    }
  };

  const searchCatalog = async () => {
    if (searchText.trim().length < 2) {
      setSearchItems([]);
      setSearchMessage("Enter at least two characters to search the catalog.");
      return;
    }
    setCatalogBusy(true);
    try {
      const data = await api<{
        items: TaxonomyItem[];
        noExactMatch: boolean;
      }>(
        `/api/taxonomy/search?surface=profile&q=${encodeURIComponent(searchText.trim())}&limit=12`,
      );
      setSearchItems(data.items);
      mergeKnownItems(data.items);
      setSearchMessage(
        data.noExactMatch
          ? "No approved catalog match yet. You can suggest a phrase for private review below."
          : null,
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to search skills.");
    } finally {
      setCatalogBusy(false);
    }
  };

  const toggleMode = (skillId: string, modeId: string) =>
    setModesBySkill((current) => {
      const selectedModes = current[skillId] || [];
      return {
        ...current,
        [skillId]: selectedModes.includes(modeId)
          ? selectedModes.filter((id) => id !== modeId)
          : [...selectedModes, modeId],
      };
    });

  const toggleFacet = (skill: TaxonomyItem, facet: TaxonomyFacet) => {
    setFacetsBySkill((current) => {
      const currentFacets = current[skill.id] || [];
      if (currentFacets.includes(facet.id))
        return {
          ...current,
          [skill.id]: currentFacets.filter((id) => id !== facet.id),
        };
      const sameDimension = (skill.facetOptions || []).filter(
        (option) =>
          option.dimensionId === facet.dimensionId &&
          currentFacets.includes(option.id),
      );
      if (sameDimension.length >= (facet.maxValues || 1)) {
        setError(
          `Choose up to ${facet.maxValues || 1} option${facet.maxValues === 1 ? "" : "s"} for ${facet.dimensionLabel || "this detail"}.`,
        );
        return current;
      }
      return { ...current, [skill.id]: [...currentFacets, facet.id] };
    });
  };

  const submitPhrase = async () => {
    if (phrase.trim().length < 3) {
      setError("Add a short phrase before sending it for private review.");
      return;
    }
    try {
      await api("/api/taxonomy/suggestions", {
        method: "POST",
        body: JSON.stringify({ surface: "profile", phrase: phrase.trim() }),
      });
      setPhraseSent(true);
      setPhrase("");
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to send that phrase.",
      );
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected.length) {
      setError("Choose at least one approved skill before saving.");
      return;
    }
    if (revision === null || !taxonomyVersion) {
      setError("Your catalog details are still loading. Please try again shortly.");
      return;
    }
    setSaving(true);
    try {
      const savedResult = await api<{ revision: number; taxonomyVersion: string }>(
        "/api/skills",
        {
          method: "PUT",
          body: JSON.stringify({
            revision,
            taxonomyVersion,
            experienceNote: description,
            skills: selected.map((categoryId) => ({
              categoryId,
              modeIds: modesBySkill[categoryId] || [],
              facetValueIds: facetsBySkill[categoryId] || [],
              source: sources[categoryId] || "description",
            })),
            areas: [{ postalCode, onlineAnywhere }],
          }),
        },
      );
      setRevision(savedResult.revision);
      setTaxonomyVersion(savedResult.taxonomyVersion);
      setSuccess(true);
      setError(null);
      await reloadSaved();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to save your skills.",
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedItems = selected
    .map((id) => knownItems[id])
    .filter((item): item is TaxonomyItem => Boolean(item));
  const visibleCatalogItems = searchItems.length ? searchItems : browseItems;
  const catalogHeading = searchItems.length
    ? "Search results"
    : browseParent
      ? "Skills in this area"
      : "Browse all skill areas";

  return (
    <AppFrame title="What do you know how to help with?">
      <p className="lede">
        Start in your own words, then confirm skills from an approved catalog.
        You stay in control of what is saved and how you want to help.
      </p>
      <form className="form-card taxonomy-form" onSubmit={submit}>
        <ErrorBox error={savedError || error} />
        {legacyPolicyNotice && <Notice>{legacyPolicyNotice}</Notice>}
        <label className="field">
          <span>Skills and experience</span>
          <textarea
            required
            minLength={8}
            maxLength={5000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="I've worked on Honda and Suzuki motorcycles for years, mostly carbs, electrical troubleshooting and older bikes. I can usually help online and sometimes locally."
          />
          <span className="help">
            Up to 5,000 characters. Your description helps find ideas; it does
            not create a public category.
          </span>
        </label>
        <button
          className="button"
          type="button"
          disabled={suggesting || loadingSaved}
          onClick={() => void suggestSkills()}
        >
          {loadingSaved || suggesting ? "Finding ideas…" : "Find skill ideas"}
        </button>

        {(ideas.length > 0 || guidance || restrictedMessage) && (
          <section className="taxonomy-panel" aria-live="polite">
            <div className="taxonomy-panel-heading">
              <div>
                <p className="eyebrow">From your description</p>
                <h3>Skill ideas to confirm</h3>
              </div>
              <span className="taxonomy-count">{ideas.length} found</span>
            </div>
            {ideas.length > 0 && (
              <div className="taxonomy-results">
                {ideas.map((item) => (
                  <article className="taxonomy-result" key={item.id}>
                    <div>
                      <strong>{item.name}</strong>
                      {item.reason && <p className="quiet">{item.reason}</p>}
                      {item.scopeNote && (
                        <p className="taxonomy-scope">{item.scopeNote}</p>
                      )}
                    </div>
                    <button
                      className="button small"
                      type="button"
                      aria-pressed={selected.includes(item.id)}
                      onClick={() => addSkill(item, "description")}
                    >
                      {selected.includes(item.id) ? "Added" : "Add skill"}
                    </button>
                  </article>
                ))}
              </div>
            )}
            {guidance && <p className="help">{guidance.message}</p>}
            {guidance?.clarification && (
              <div className="taxonomy-clarification">
                <label className="field">
                  <span>{guidance.clarification.prompt}</span>
                  <input
                    value={clarification}
                    onChange={(e) => setClarification(e.target.value)}
                    placeholder={guidance.clarification.examples[0]}
                  />
                  <small className="help">
                    For example: {guidance.clarification.examples.join(" · ")}
                  </small>
                </label>
                <button
                  className="button small"
                  type="button"
                  disabled={suggesting || clarification.trim().length < 3}
                  onClick={() => void suggestSkills(true)}
                >
                  Use this example
                </button>
              </div>
            )}
            {restrictedMessage && (
              <div className="taxonomy-restricted" role="status">
                <strong>Outside ordinary peer matching</strong>
                <p>{restrictedMessage}</p>
              </div>
            )}
          </section>
        )}

        <section className="taxonomy-discovery" aria-label="Approved skills catalog">
          <div className="taxonomy-panel-heading">
            <div>
              <p className="eyebrow">Approved catalog</p>
              <h3>Find a specific skill</h3>
            </div>
          </div>
          <div className="taxonomy-search">
            <label className="visually-hidden" htmlFor="skill-catalog-search">
              Search approved skills
            </label>
            <input
              id="skill-catalog-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void searchCatalog();
                }
              }}
              placeholder="Search: phone setup, garden, sewing…"
            />
            <button
              className="button small"
              type="button"
              disabled={catalogBusy}
              onClick={() => void searchCatalog()}
            >
              Search
            </button>
            {searchItems.length > 0 && (
              <button
                className="button tertiary"
                type="button"
                onClick={() => {
                  setSearchItems([]);
                  setSearchMessage(null);
                  setSearchText("");
                }}
              >
                Clear search
              </button>
            )}
          </div>
          {browseParent && !searchItems.length && (
            <button
              className="button tertiary"
              type="button"
              onClick={() => void loadBrowse(null)}
            >
              ← All skill areas
            </button>
          )}
          <h4 className="taxonomy-subheading">{catalogHeading}</h4>
          {catalogBusy ? (
            <p className="quiet">Loading catalog…</p>
          ) : (
            <div className="taxonomy-catalog-grid">
              {visibleCatalogItems.map((item) =>
                item.nodeKind === "domain" ? (
                  <button
                    className="taxonomy-domain"
                    type="button"
                    key={item.id}
                    onClick={() => void loadBrowse(item.id)}
                  >
                    <strong>{item.name}</strong>
                    <span>{item.description || "Browse skills"}</span>
                    <small>Browse area →</small>
                  </button>
                ) : (
                  <article className="taxonomy-catalog-item" key={item.id}>
                    <div>
                      <strong>{item.name}</strong>
                      {item.path.length > 1 && (
                        <span className="taxonomy-path">
                          {item.path.slice(0, -1).join(" › ")}
                        </span>
                      )}
                      {item.scopeNote && (
                        <p className="taxonomy-scope">{item.scopeNote}</p>
                      )}
                    </div>
                    <button
                      className="button small"
                      type="button"
                      aria-pressed={selected.includes(item.id)}
                      onClick={() =>
                        addSkill(item, searchItems.length ? "search" : "browse")
                      }
                    >
                      {selected.includes(item.id) ? "Added" : "Add"}
                    </button>
                  </article>
                ),
              )}
            </div>
          )}
          {searchMessage && <p className="help">{searchMessage}</p>}
          {(guidance?.state === "no_match" || searchMessage) && (
            <div className="taxonomy-phrase">
              <label className="field">
                <span>I do not see it</span>
                <input
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder="A short name for the skill or task"
                />
                <small className="help">
                  This is encrypted for private catalog review. It is not made
                  public or added as a skill automatically.
                </small>
              </label>
              <button
                className="button small"
                type="button"
                onClick={() => void submitPhrase()}
              >
                Send for review
              </button>
              {phraseSent && (
                <p className="taxonomy-phrase-success" role="status">
                  Thanks — it is queued for private catalog review.
                </p>
              )}
            </div>
          )}
        </section>

        <section className="taxonomy-selected" aria-labelledby="selected-skills-title">
          <div className="taxonomy-panel-heading">
            <div>
              <p className="eyebrow">Your profile</p>
              <h3 id="selected-skills-title">Skills you have confirmed</h3>
            </div>
            <span className="taxonomy-count">{selectedItems.length} selected</span>
          </div>
          <p className="help">
            Only these approved skills are saved to your profile and can affect
            matching. Pick the practical ways you want to help for each one.
          </p>
          {selectedItems.length ? (
            <div className="taxonomy-selected-list">
              {selectedItems.map((item) => {
                const facetsByDimension = (item.facetOptions || []).reduce<
                  Record<string, TaxonomyFacet[]>
                >((groups, facet) => {
                  groups[facet.dimensionId] = [
                    ...(groups[facet.dimensionId] || []),
                    facet,
                  ];
                  return groups;
                }, {});
                const modeOptions = item.modeOptions || [];
                return (
                  <article className="taxonomy-selected-card" key={item.id}>
                    <div className="taxonomy-selected-title">
                      <div>
                        <h4>{item.name}</h4>
                        {item.scopeNote && (
                          <p className="taxonomy-scope">{item.scopeNote}</p>
                        )}
                      </div>
                      <button
                        className="button tertiary"
                        type="button"
                        onClick={() => removeSkill(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                    {modeOptions.length > 0 && (
                      <fieldset className="taxonomy-choice-field">
                        <legend>How could you help with this?</legend>
                        <div className="chip-row">
                          {modeOptions.map((mode) => (
                            <button
                              className="chip"
                              type="button"
                              key={mode.id}
                              aria-pressed={(modesBySkill[item.id] || []).includes(
                                mode.id,
                              )}
                              onClick={() => toggleMode(item.id, mode.id)}
                              title={mode.description}
                            >
                              {mode.label}
                            </button>
                          ))}
                        </div>
                      </fieldset>
                    )}
                    {Object.entries(facetsByDimension).map(
                      ([dimensionId, options]) => (
                        <fieldset className="taxonomy-choice-field" key={dimensionId}>
                          <legend>{options[0]?.dimensionLabel || "Details"}</legend>
                          <div className="chip-row">
                            {options.map((facet) => (
                              <button
                                className="chip"
                                type="button"
                                key={facet.id}
                                aria-pressed={(
                                  facetsBySkill[item.id] || []
                                ).includes(facet.id)}
                                onClick={() => toggleFacet(item, facet)}
                                title={facet.description || undefined}
                              >
                                {facet.label}
                              </button>
                            ))}
                          </div>
                        </fieldset>
                      ),
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="taxonomy-empty">
              No skills selected yet. Use ideas, search, or browse to choose at
              least one approved skill.
            </div>
          )}
        </section>

        <div className="taxonomy-availability">
          <label className="field">
            <span>Service postal code</span>
            <input
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={onlineAnywhere}
              onChange={(e) => setOnlineAnywhere(e.target.checked)}
            />
            <span>I can offer online help anywhere.</span>
          </label>
        </div>
        {success && (
          <div className="alert success" role="status">
            Your controlled skills and availability were saved.
          </div>
        )}
        <button className="button primary" disabled={saving || loadingSaved}>
          {saving ? "Saving…" : "Save confirmed skills"}
        </button>
      </form>
    </AppFrame>
  );
}
function Matches() {
  const [data, loading, error, reload] = useAsyncData<{ matches: Match[] }>(
    "/api/matches",
    { matches: [] },
  );
  const navigate = useNavigate();
  const [replying, setReplying] = useState<string | null>(null);
  const [message, setMessage] = useState(
    "I may be able to help you think this through. What have you already checked?",
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const respond = async (matchId: string) => {
    try {
      const created = await api<{ conversationId: string }>(
        "/api/matches/respond",
        { method: "POST", body: JSON.stringify({ matchId, message }) },
      );
      navigate(`/conversations/${created.conversationId}`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unable to respond.");
    }
  };
  const decline = async (matchId: string) => {
    try {
      await api(`/api/matches/${matchId}/decline`, { method: "POST" });
      setReplying(null);
      await reload();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Unable to pass on this match.",
      );
    }
  };
  return (
    <AppFrame title="Possible matches">
      <p className="lede">
        These are private possible fits based on your skills, availability, and
        service area. You are always free to pass.
      </p>
      <ErrorBox error={error || actionError} />
      {loading ? (
        <div className="loading">Loading possible matches…</div>
      ) : data.matches.length ? (
        <div className="list">
          {data.matches.map((match) => (
            <article className="card match-card" key={match.id}>
              <div>
                <span className="status">{match.status}</span>
                <h3>{match.title}</h3>
                <p>{match.publicSummary}</p>
                <p className="reason">
                  Why this may fit:{" "}
                  {match.reasonCodes.includes("exact_skill_match")
                    ? "Matches skills you selected"
                    : "May relate to your selected skills"}
                  {match.reasonCodes.includes("within_service_area")
                    ? " and your service area"
                    : ""}
                  .
                </p>
                {replying === match.id && (
                  <label className="field">
                    <span>Your first response</span>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </label>
                )}
              </div>
              <div>
                {match.status === "responded" ? (
                  <Link className="button primary" to="/conversations">
                    Open conversation
                  </Link>
                ) : replying === match.id ? (
                  <>
                    <button
                      className="button primary"
                      onClick={() => void respond(match.id)}
                    >
                      Send response
                    </button>
                    <button
                      className="button tertiary"
                      onClick={() => setReplying(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="button primary"
                    onClick={() => setReplying(match.id)}
                  >
                    Respond
                  </button>
                )}
                <button
                  className="button tertiary"
                  onClick={() => void decline(match.id)}
                >
                  Not for me
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="card empty">
          No possible matches right now. When a relevant request arrives, it
          will show here. You can pause all new requests in Settings.
        </div>
      )}
    </AppFrame>
  );
}
function Conversations() {
  const [data, loading, error] = useAsyncData<{
    conversations: ConversationListItem[];
  }>("/api/conversations", { conversations: [] });
  return (
    <AppFrame title="Conversations">
      <p className="lede">
        Site conversations are the canonical record. Account emails remain
        private unless a person explicitly shares one.
      </p>
      <ErrorBox error={error} />
      {loading ? (
        <div className="loading">Loading conversations…</div>
      ) : data.conversations.length ? (
        <div className="card list">
          {data.conversations.map((conversation) => (
            <Link
              className="conversation-link"
              key={conversation.id}
              to={`/conversations/${conversation.id}`}
            >
              <strong>{conversation.otherName || "Conversation"}</strong>
              <br />
              <span className="quiet">
                {conversation.title} · {formatDate(conversation.lastMessageAt)}
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <div className="card empty">
          No conversations yet. They begin when someone chooses to respond to a
          relevant request.
        </div>
      )}
    </AppFrame>
  );
}
function TrustEvidence({
  actorId,
  requestId,
}: {
  actorId: string;
  requestId: string;
}) {
  const [data, loading] = useAsyncData<{
    heading: string;
    statements: string[];
    boundaries: string;
  }>(`/api/trust/${actorId}?requestId=${requestId}`, {
    heading: "",
    statements: [],
    boundaries: "",
  });
  if (loading || !data.heading) return null;
  return (
    <aside className="card subtle" aria-label="Contextual interaction evidence">
      <h3>{data.heading}</h3>
      {data.statements.map((statement) => (
        <p key={statement}>{statement}</p>
      ))}
      <p className="quiet">{data.boundaries}</p>
    </aside>
  );
}

function Conversation() {
  const { conversationId = "" } = useParams();
  const { user } = useAuth();
  const [data, loading, error, reload] = useAsyncData<ConversationDetail>(
    `/api/conversations/${conversationId}`,
    {
      conversation: { id: "", status: "", requestId: "", participants: [] },
      messages: [],
    },
  );
  const [body, setBody] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareType, setShareType] = useState("phone");
  const [shareValue, setShareValue] = useState("");
  const [reporting, setReporting] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reportCategory, setReportCategory] = useState("harassment");
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const other = data.conversation.participants.find(
    (item) => item.actorId !== user?.actorId,
  );
  const send = async (event: FormEvent) => {
    event.preventDefault();
    setSending(true);
    try {
      await api(`/api/conversations/${conversationId}/messages`, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      setBody("");
      await reload();
      composerRef.current?.focus();
    } catch (err) {
      setSendError(
        err instanceof Error ? err.message : "Unable to send this message.",
      );
    } finally {
      setSending(false);
    }
  };
  const block = async () => {
    if (!other) return;
    try {
      await api(`/api/conversations/${conversationId}/block`, {
        method: "POST",
        body: JSON.stringify({ actorId: other.actorId }),
      });
      await reload();
    } catch (err) {
      setSendError(
        err instanceof Error
          ? err.message
          : "Unable to close this conversation.",
      );
    }
  };
  const share = async (event: FormEvent) => {
    event.preventDefault();
    if (!other) return;
    try {
      await api(`/api/conversations/${conversationId}/contacts`, {
        method: "POST",
        body: JSON.stringify({
          toActorId: other.actorId,
          type: shareType,
          value: shareValue,
          confirmed: true,
        }),
      });
      setShareValue("");
      setSharing(false);
    } catch (err) {
      setSendError(
        err instanceof Error ? err.message : "Unable to share that detail.",
      );
    }
  };
  const report = async (event: FormEvent) => {
    event.preventDefault();
    if (!other) return;
    try {
      await api(`/api/conversations/${conversationId}/report`, {
        method: "POST",
        body: JSON.stringify({
          reportedActorId: other.actorId,
          category: reportCategory,
          description: reportText,
        }),
      });
      setReporting(false);
      setReportText("");
    } catch (err) {
      setSendError(
        err instanceof Error ? err.message : "Unable to submit your report.",
      );
    }
  };
  if (loading)
    return (
      <AppFrame title="Conversation">
        <div className="loading">Loading conversation…</div>
      </AppFrame>
    );
  if (error)
    return (
      <AppFrame title="Conversation">
        <ErrorBox error={error} />
      </AppFrame>
    );
  return (
    <AppFrame
      title={
        data.conversation.participants
          .map((item) => item.displayName)
          .filter(Boolean)
          .join(" and ") || "Conversation"
      }
    >
      <div className="card thread">
        <header className="thread-header">
          <span
            className={`status ${data.conversation.status === "active" ? "" : "closed"}`}
          >
            {data.conversation.status}
          </span>
          <p className="quiet">
            Private site conversation · contact details are separate, explicit
            choices.
          </p>
          {data.conversation.status === "active" && (
            <div>
              <button
                className="button small"
                onClick={() => setSharing(!sharing)}
              >
                Share contact or meeting details
              </button>
              <button
                className="button tertiary"
                onClick={() => setReporting(!reporting)}
              >
                Report
              </button>
              <button className="button tertiary" onClick={() => void block()}>
                Block and close
              </button>
            </div>
          )}
        </header>
        {other && (
          <TrustEvidence
            actorId={other.actorId}
            requestId={data.conversation.requestId}
          />
        )}
        {sharing && (
          <form className="form-card" onSubmit={share}>
            <h3>Share one detail explicitly</h3>
            <p className="help">
              You choose the detail and recipient. Consider meeting in a public
              place when appropriate; Stuff.Repair does not guarantee physical
              safety.
            </p>
            <label className="field">
              <span>Detail type</span>
              <select
                value={shareType}
                onChange={(e) => setShareType(e.target.value)}
              >
                <option value="phone">Phone</option>
                <option value="email">Email</option>
                <option value="address">Meeting address</option>
                <option value="location_note">Location note</option>
              </select>
            </label>
            <label className="field">
              <span>Detail to share</span>
              <input
                required
                value={shareValue}
                onChange={(e) => setShareValue(e.target.value)}
              />
            </label>
            <button className="button primary">Confirm and share</button>
          </form>
        )}
        {reporting && (
          <form className="form-card" onSubmit={report}>
            <h3>Report a concern</h3>
            <label className="field">
              <span>Category</span>
              <select
                value={reportCategory}
                onChange={(e) => setReportCategory(e.target.value)}
              >
                <option value="harassment">Harassment</option>
                <option value="threat">Threat</option>
                <option value="scam_fraud">Scam or fraud</option>
                <option value="unsafe_behavior">
                  Unsafe instruction or behavior
                </option>
                <option value="unwanted_contact">Unwanted contact</option>
                <option value="impersonation">Impersonation</option>
                <option value="inappropriate_behavior">
                  Sexual or inappropriate behavior
                </option>
                <option value="discriminatory_abuse">
                  Discriminatory abuse
                </option>
                <option value="spam">Spam</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="field">
              <span>What happened?</span>
              <textarea
                required
                minLength={5}
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
              />
            </label>
            <button className="button danger">Submit private report</button>
          </form>
        )}
        <div className="messages" aria-live="polite" aria-label="Messages">
          {data.messages.length ? (
            data.messages.map((message) => (
              <article
                className={`message ${message.senderActorId === user?.actorId ? "mine" : ""}`}
                key={message.id}
              >
                <strong>
                  {message.senderActorId === user?.actorId
                    ? "You"
                    : message.senderName}
                </strong>
                <br />
                {message.body}
                <div className="message-meta">
                  {formatDate(message.createdAt)} ·{" "}
                  {message.transport === "email" ? "Email reply" : "Sent"}
                </div>
              </article>
            ))
          ) : (
            <div className="empty">No messages yet.</div>
          )}
        </div>
        <ErrorBox error={sendError} />
        {data.conversation.status === "active" ? (
          <form className="composer" onSubmit={send}>
            <label className="visually-hidden" htmlFor="message-body">
              Message
            </label>
            <textarea
              ref={composerRef}
              id="message-body"
              aria-label="Write a message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a message…"
            />
            <button className="button primary" disabled={sending}>
              {sending ? "Sending…" : "Send"}
            </button>
          </form>
        ) : (
          <div className="alert">This conversation is closed.</div>
        )}
      </div>
    </AppFrame>
  );
}
function Settings() {
  const { refresh } = useAuth();
  const [details, loading, error] = useAsyncData<{
    user: User & {
      inApp?: number;
      webPush?: number;
      emailNotify?: number;
      emailBridge?: number;
      previewLevel?: string;
      unreadDelay?: number;
      pausedUntil?: string | null;
    };
  }>("/api/me", { user: {} as User });
  const [prefs, setPrefs] = useState({
    inApp: true,
    webPush: false,
    emailNotify: true,
    emailBridge: false,
    previewLevel: "normal",
    unreadDelay: 20,
  });
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pause, setPause] = useState("active");
  const [until, setUntil] = useState("");
  const [deletion, setDeletion] = useState<{ developmentLink?: string } | null>(
    null,
  );
  useEffect(() => {
    if (details.user.actorId)
      setPrefs({
        inApp: Boolean(details.user.inApp),
        webPush: Boolean(details.user.webPush),
        emailNotify: Boolean(details.user.emailNotify),
        emailBridge: Boolean(details.user.emailBridge),
        previewLevel: details.user.previewLevel || "normal",
        unreadDelay: details.user.unreadDelay || 20,
      });
    setPause(details.user.availabilityStatus || "active");
  }, [details]);
  const savePrefs = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/settings/preferences", {
        method: "PATCH",
        body: JSON.stringify(prefs),
      });
      setSaved(true);
      setSaveError(null);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Unable to save preferences.",
      );
    }
  };
  const saveAvailability = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/settings/availability", {
        method: "PATCH",
        body: JSON.stringify({ mode: pause, until }),
      });
      await refresh();
      setSaved(true);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Unable to update availability.",
      );
    }
  };
  const requestDeletion = async () => {
    try {
      setDeletion(
        await api<{ developmentLink?: string }>("/api/me/deletion/request", {
          method: "POST",
        }),
      );
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Unable to start deletion.",
      );
    }
  };
  return (
    <AppFrame title="Settings">
      <ErrorBox error={error || saveError} />
      {loading ? (
        <div className="loading">Loading settings…</div>
      ) : (
        <div className="settings-grid">
          <nav className="settings-list card" aria-label="Settings sections">
            <a href="#notifications">Notifications</a>
            <a href="#availability">Availability</a>
            <a href="#privacy">Privacy</a>
            <a href="#data">My data</a>
          </nav>
          <div>
            <ProfileControls />
            <section className="card" id="notifications">
              <h2>Notifications</h2>
              <form onSubmit={savePrefs}>
                {[
                  ["inApp", "In-app notifications"],
                  ["webPush", "Web push (when supported)"],
                  ["emailNotify", "Email reminders for unread activity"],
                  ["emailBridge", "Let me read and reply by email"],
                ].map(([key, label]) => (
                  <label className="check-row" key={key}>
                    <input
                      type="checkbox"
                      checked={prefs[key as keyof typeof prefs] === true}
                      onChange={(e) =>
                        setPrefs({ ...prefs, [key]: e.target.checked })
                      }
                    />
                    <span>
                      {label}
                      {key === "emailBridge" && (
                        <small className="help">
                          <br />
                          This is optional. A reply address is opaque and the
                          other participant’s email is never included.
                        </small>
                      )}
                    </span>
                  </label>
                ))}
                <label className="field">
                  <span>Notification privacy</span>
                  <select
                    value={prefs.previewLevel}
                    onChange={(e) =>
                      setPrefs({ ...prefs, previewLevel: e.target.value })
                    }
                  >
                    <option value="private">
                      Private — “You have a new message”
                    </option>
                    <option value="normal">
                      Normal — who replied and the request
                    </option>
                    <option value="full">Full — may include preview</option>
                  </select>
                </label>
                <button className="button primary">
                  Save notification preferences
                </button>
              </form>
            </section>
            <section
              className="card"
              id="availability"
              style={{ marginTop: "1rem" }}
            >
              <h2>Pause or take time away</h2>
              <p>
                Existing conversations remain available while you pause
                matching.
              </p>
              <form onSubmit={saveAvailability}>
                <label className="field">
                  <span>Availability</span>
                  <select
                    value={pause}
                    onChange={(e) => setPause(e.target.value)}
                  >
                    <option value="active">Available for new requests</option>
                    <option value="paused">Pause new requests</option>
                    <option value="vacation">Vacation / unavailable</option>
                  </select>
                </label>
                {pause !== "active" && (
                  <label className="field">
                    <span>Resume date (optional)</span>
                    <input
                      type="datetime-local"
                      value={until}
                      onChange={(e) => setUntil(e.target.value)}
                    />
                  </label>
                )}
                <button className="button primary">Save availability</button>
              </form>
            </section>
            <section
              className="card"
              id="privacy"
              style={{ marginTop: "1rem" }}
            >
              <h2>Privacy and blocks</h2>
              <p>
                Your general area can be used for matching without displaying
                your exact home location. Direct contact sharing is always an
                explicit action in a conversation.
              </p>
              <p>
                <strong>Blocked people</strong> cannot create new normal matches
                or messages with you. Existing conversations close neutrally.
              </p>
            </section>
            <section className="card" id="data" style={{ marginTop: "1rem" }}>
              <h2>My data</h2>
              <p>
                You can download eligible account data in an understandable
                format. Account deletion stops discoverability and schedules
                deletion/anonymization after a short grace period, subject to
                records required for security, abuse prevention, legal
                obligations, or unresolved reports.
              </p>
              <a className="button" href="/api/me/export">
                Download my data
              </a>
              <button
                className="button danger"
                style={{ marginLeft: ".6rem" }}
                onClick={() => void requestDeletion()}
              >
                Delete my account
              </button>
              {deletion && (
                <div className="alert">
                  <strong>
                    Check your email for a fresh confirmation link.
                  </strong>
                  {deletion.developmentLink && (
                    <>
                      {" "}
                      In local development:{" "}
                      <Link to={deletion.developmentLink}>
                        confirm deletion securely
                      </Link>
                      .
                    </>
                  )}
                </div>
              )}
            </section>
            {saved && (
              <div className="alert success" role="status">
                Your settings were saved.
              </div>
            )}
          </div>
        </div>
      )}
    </AppFrame>
  );
}
function ProfileControls() {
  const [data, loading] = useAsyncData<{ user: User & { bio?: string } }>(
    "/api/me",
    { user: {} as User },
  );
  const [blocks, blocksLoading, blocksError, reloadBlocks] = useAsyncData<{
    blocks: Array<{ actorId: string; displayName: string; createdAt: string }>;
  }>("/api/me/blocks", { blocks: [] });
  const [form, setForm] = useState({
    displayName: "",
    locality: "",
    postalCode: "",
    bio: "",
  });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (data.user.actorId)
      setForm({
        displayName: data.user.displayName || "",
        locality: data.user.locality || "",
        postalCode: data.user.postalCode || "",
        bio: data.user.bio || "",
      });
  }, [data]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/me/profile", {
        method: "PATCH",
        body: JSON.stringify(form),
      });
      setMessage("Your profile was saved.");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save profile.");
    }
  };
  const unblock = async (actorId: string) => {
    try {
      await api(`/api/me/blocks/${actorId}`, { method: "DELETE" });
      await reloadBlocks();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to unblock that person.",
      );
    }
  };
  if (loading)
    return (
      <section className="card">
        <p>Loading your profile…</p>
      </section>
    );
  return (
    <>
      <section className="card" id="profile">
        <h2>My profile</h2>
        <p>
          Your display name and general area may be used in a relevant
          connection. Your account email remains private.
        </p>
        <form onSubmit={save}>
          <label className="field">
            <span>Display name</span>
            <input
              required
              value={form.displayName}
              onChange={(e) =>
                setForm({ ...form, displayName: e.target.value })
              }
            />
          </label>
          <label className="field">
            <span>Approximate locality</span>
            <input
              value={form.locality}
              onChange={(e) => setForm({ ...form, locality: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Postal code</span>
            <input
              value={form.postalCode}
              onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Short bio (optional)</span>
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
            />
          </label>
          <ErrorBox error={error} />
          {message && (
            <div className="alert success" role="status">
              {message}
            </div>
          )}
          <button className="button primary">Save profile</button>
        </form>
      </section>
      <section className="card" style={{ marginTop: "1rem" }}>
        <h2>Blocked people</h2>
        <p>
          Blocks stop normal future matching and close active conversations. The
          other person is not told that you blocked them.
        </p>
        <ErrorBox error={blocksError} />
        {blocksLoading ? (
          <p className="quiet">Loading blocked people…</p>
        ) : blocks.blocks.length ? (
          <div className="list">
            {blocks.blocks.map((block) => (
              <div className="list-item" key={block.actorId}>
                <span>{block.displayName}</span>
                <button
                  className="button small"
                  onClick={() => void unblock(block.actorId)}
                >
                  Remove block
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="quiet">You have not blocked anyone.</p>
        )}
      </section>
    </>
  );
}

function DeleteAccount() {
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const navigate = useNavigate();
  const [acknowledgement, setAcknowledgement] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/me/deletion/confirm", {
        method: "POST",
        body: JSON.stringify({ token, acknowledgement }),
      });
      setDone(true);
      setTimeout(() => navigate("/"), 1400);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to confirm deletion.",
      );
    }
  };
  return (
    <div className="page narrow">
      <p className="eyebrow">Account deletion</p>
      <h1>Confirm account deletion.</h1>
      <div className="form-card">
        <p>
          Your profile becomes unavailable, new matching stops, active
          conversations close, sessions and email relay aliases are revoked, and
          eligible direct personal data is scheduled for deletion or
          anonymization. This cannot always be reversed.
        </p>
        <form onSubmit={submit}>
          <label className="field">
            <span>
              Type <code>DELETE MY ACCOUNT</code> to confirm
            </span>
            <input
              value={acknowledgement}
              onChange={(e) => setAcknowledgement(e.target.value)}
            />
          </label>
          <ErrorBox error={error} />
          {done ? (
            <div className="alert success">
              Deletion is scheduled. You are being signed out.
            </div>
          ) : (
            <button className="button danger">Confirm account deletion</button>
          )}
        </form>
      </div>
    </div>
  );
}
function Admin() {
  const [data, loading, error] = useAsyncData<{
    reports: Array<{
      id: string;
      category: string;
      status: string;
      severity: string;
      reporterName: string;
      reportedName: string;
      createdAt: string;
    }>;
  }>("/api/admin/reports", { reports: [] });
  return (
    <AppFrame title="Moderation">
      <p className="lede">
        Authorized moderation work is private. Access to conversation content
        requires a case reason and generates an audit event.
      </p>
      <ErrorBox error={error} />
      {loading ? (
        <div className="loading">Loading reports…</div>
      ) : (
        <div className="card table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Status</th>
                <th>Severity</th>
                <th>Reporter</th>
                <th>Reported</th>
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {data.reports.map((report) => (
                <tr key={report.id}>
                  <td>{report.category.replaceAll("_", " ")}</td>
                  <td>{report.status}</td>
                  <td>{report.severity}</td>
                  <td>{report.reporterName}</td>
                  <td>{report.reportedName}</td>
                  <td>{formatDate(report.createdAt)}</td>
                </tr>
              ))}
              {!data.reports.length && (
                <tr>
                  <td colSpan={6}>No reports are available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </AppFrame>
  );
}
function LocationPage() {
  const { postalCode = "" } = useParams();
  const [data, loading, error] = useAsyncData<{
    postalCode: string;
    categories: string[];
    indexable: boolean;
    explanation: string;
  }>(`/api/public/location/${postalCode}`, {
    postalCode,
    categories: [],
    indexable: false,
    explanation: "",
  });
  return (
    <div className="page narrow">
      <p className="eyebrow">Local commons</p>
      <h1>Practical help near {postalCode}</h1>
      {loading ? (
        <div className="loading">Loading local information…</div>
      ) : error ? (
        <Notice>
          We do not have a public location summary for this area yet. You can
          still ask for help.
        </Notice>
      ) : (
        <>
          <p className="lede">{data.explanation}</p>
          {data.categories.length ? (
            <div className="card">
              <h2>Community coverage</h2>
              <p>
                People in this area have opted in to help with:{" "}
                {data.categories.join(", ")}.
              </p>
            </div>
          ) : (
            <div className="card">
              <p>
                This page does not publish private member information or a
                directory of people.
              </p>
            </div>
          )}
          <PublicCta />
        </>
      )}
    </div>
  );
}
function NotFound() {
  return (
    <div className="page narrow">
      <p className="eyebrow">Not found</p>
      <h1>That page is not here.</h1>
      <p>Try the main page or ask for help with a practical problem.</p>
      <Link className="button primary" to="/">
        Go to Stuff.Repair
      </Link>
    </div>
  );
}

export default App;
